import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import config_names from './config_names';
import { 
  FiAlertCircle, 
  FiCheckCircle, 
  FiEye, 
  FiEyeOff, 
  FiSave, 
  FiArrowLeft 
} from 'react-icons/fi';
import './auth-pages.css';

function ResetPasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [canReset, setCanReset] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;
        setCanReset(Boolean(session));
      } catch (sessionError) {
        if (isMounted) {
          setError(sessionError?.message || 'Unable to validate your reset link.');
        }
      } finally {
        if (isMounted) {
          setChecking(false);
        }
      }
    };

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      if (event === 'PASSWORD_RECOVERY' && session) {
        setCanReset(true);
        setChecking(false);
      }
    });

    checkSession();

    return () => {
      isMounted = false;
      if (authListener?.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const updatePassword = async () => {
    setError('');
    setSuccess('');

    if (!canReset) {
      setError('This reset link is invalid or has expired. Please request a new one.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      setSuccess('Your password has been updated. Please sign in again.');
      await supabase.auth.signOut();
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      setError(error?.message || 'Failed to update password.');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Reset Password</h1>
          <p>Set a new password for {config_names.projectName}</p>
        </div>

        {error && (
          <div className="auth-error">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="auth-success">
            <FiCheckCircle />
            <span>{success}</span>
          </div>
        )}

        {checking && (
          <div className="auth-warning">
            <FiAlertCircle />
            <span>Validating your reset link...</span>
          </div>
        )}

        {!checking && !canReset && !success && (
          <div className="auth-warning">
            <FiAlertCircle />
            <span>This reset link is invalid or expired.</span>
          </div>
        )}

        {!checking && !canReset && !success && (
          <div className="auth-footer">
            <Link to="/forgot-password">Request a new reset link</Link>
          </div>
        )}

        {canReset && !success && (
          <form
            className="auth-form"
            onSubmit={(event) => {
              event.preventDefault();
              updatePassword();
            }}
          >
            <div className="form-group">
              <div className="input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-btn link-btn"
                  onClick={togglePasswordVisibility}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <div className="input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="form-control"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-btn link-btn"
                  onClick={toggleConfirmPasswordVisibility}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <button type="submit" className="auth-btn primary-btn">
              <FiSave /> Update Password
            </button>

            <Link to="/login" className="auth-btn secondary-btn">
              <FiArrowLeft /> Back to Sign In
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordPage;
